import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { patchState, signalState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, pipe, switchMap, tap } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { DEFAULT_TEMPLATE_KEY, LoadStatus } from '@capital-access/common/utils';
import { loadTemplateAction } from '@capital-access/reporting/common';
import {
  ReportTemplate,
  ReportType,
  sortTemplateByDateDesc,
  TemplateLibraryService
} from '@capital-access/reporting/common';

type TemplatesState = {
  templates: ReportTemplate[];
  loadStatus: LoadStatus;
  selectedTemplate: ReportTemplate | null;
  defaultTemplateId: number;
  myTeamTemplateCount: number;
};

const initialState: TemplatesState = {
  templates: [],
  loadStatus: LoadStatus.Pristine,
  selectedTemplate: null,
  defaultTemplateId: 0,
  myTeamTemplateCount: 0
};

@Injectable()
export class ItineraryTemplatesStore {
  readonly #templatesService = inject(TemplateLibraryService);
  readonly #state = signalState(initialState);
  readonly templates = this.#state.templates;
  readonly myTeamTemplateCount = this.#state.myTeamTemplateCount;
  readonly loadStatus = this.#state.loadStatus;
  readonly selectedTemplate = this.#state.selectedTemplate;
  readonly defaultTemplateId = this.#state.defaultTemplateId;

  constructor() {
    this.listenForLoadTemplateAction();
  }

  private listenForLoadTemplateAction() {
    const actions$ = inject(Actions);
    actions$
      .pipe(
        ofType(loadTemplateAction),
        takeUntilDestroyed(),
        tap(action => {
          patchState(this.#state, { loadStatus: LoadStatus.Loading });
          this.loadTemplates(action.reportType);
        })
      )
      .subscribe();
  }

  readonly loadTemplates = rxMethod<ReportType>(
    pipe(
      filter(() => this.loadStatus() !== LoadStatus.Loaded),
      tap(reportType =>
        patchState(this.#state, {
          loadStatus: LoadStatus.Loading,
          //take a value from local to set as default template Id
          defaultTemplateId: Number(localStorage.getItem(`${reportType}_${DEFAULT_TEMPLATE_KEY}`.toUpperCase()) ?? 0)
        })
      ),
      switchMap(reportType => {
        return this.#templatesService.getTemplates().pipe(
          map(templates => {
            const myTeamTemplateCount = templates.filter(f => !f.caTemplateInd).length;
            const templatesByReportType = templates.filter(f => f.reportType == reportType && !f.caTemplateInd);
            let teamsTemplates = sortTemplateByDateDesc(templatesByReportType).filter(
              template => !template.caTemplateInd
            );
            teamsTemplates = this.setDefaultTemplate(teamsTemplates, this.defaultTemplateId());
            patchState(this.#state, {
              loadStatus: LoadStatus.Loaded,
              myTeamTemplateCount: myTeamTemplateCount,
              selectedTemplate: teamsTemplates.length ? teamsTemplates[0] : null
            });
          }),
          catchError(() => {
            patchState(this.#state, { loadStatus: LoadStatus.Failed });
            return EMPTY;
          })
        );
      })
    )
  );

  setSelectedTemplate(selectedTemplate: ReportTemplate): void {
    patchState(this.#state, { selectedTemplate });
  }

  setLoadStatus(loadStatus: LoadStatus): void {
    patchState(this.#state, { loadStatus });
  }

  //Moves the default template to the top of the list if it exists
  setDefaultTemplate(teamsTemplates: ReportTemplate[], templateId: number) {
    // if (templateId > 0) {
    //   const defaultTemplateIndex = teamsTemplates.findIndex(t => t.templateId === templateId);
    //   if (defaultTemplateIndex > -1) {
    //     const [defaultTemplate] = teamsTemplates.splice(defaultTemplateIndex, 1);
    //     teamsTemplates.unshift(defaultTemplate);
    //   } else {
    //     templateId = 0;
    //     localStorage.setItem(`${ReportType.Institution}_${SECTIONS_LAYOUT_KEY}`.toUpperCase(), DEFAULT_LAYOUT);
    //     localStorage.removeItem(`${ReportType.Institution}_${DEFAULT_TEMPLATE_KEY}`.toUpperCase());
    //   }
    // }
    patchState(this.#state, {
      templates: [...teamsTemplates],
      defaultTemplateId: templateId
    });
    return teamsTemplates;
  }
}
